import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Loader2 } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useI18n } from '../contexts/I18nProvider';
import { getAgentTranslation } from '../utils/agentTranslation';
import { useTranslation } from '../hooks/useTranslation';
import { useTenantIcons } from '../hooks/useTenantIcons';

interface HomeScreenItem {
  id: string;
  type: 'agent' | 'embedded' | 'grouping-container';
  name?: string;
  showTitle?: boolean;
  groupAccess?: string[];
  agentId?: string;
  embeddedContentId?: string;
  parentId?: string | null;
  order: number;
  isActive: boolean;
}

interface Agent {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

interface EmbeddedContent {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  embeddingUrl: string;
}

export const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useI18n();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [homeScreenItems, setHomeScreenItems] = useState<HomeScreenItem[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [embeddedContents, setEmbeddedContents] = useState<EmbeddedContent[]>([]);
  const [userGroupIds, setUserGroupIds] = useState<string[]>([]);
  const [languages, setLanguages] = useState<Array<{ id: string; code: string; isDefault?: boolean }>>([]);

  // Get tenant icons
  const tenantSlug = import.meta.env.VITE_TENANT_SLUG || 'default';
  const { getIcon } = useTenantIcons(tenantSlug);

  // Get icon components
  const Loader2Icon = getIcon('loading', 'Loader2') || Loader2;

  // Helper function to get icon component
  const getIconComponent = (iconName: string) => {
    try {
      const IconComponent = LucideIcons[iconName as keyof typeof LucideIcons] as React.ComponentType<any>;
      return IconComponent || LucideIcons.Bot;
    } catch {
      return LucideIcons.Bot;
    }
  };

  useEffect(() => {
    loadHomeScreen();
  }, []);

  const loadHomeScreen = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) {
      setError('User not authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Load all necessary data
      const [items, agentsData, embeddedData, userGroupsData, languagesData] = await Promise.all([
        apiService.getTenantHomeScreenItems(user.tenantId),
        apiService.getTenantAgents(user.tenantId),
        apiService.getTenantEmbeddedContents(user.tenantId),
        apiService.getTenantUserGroups(user.tenantId),
        apiService.getTenantLanguages(user.tenantId),
      ]);

      // Get user's group IDs
      const currentUserEmail = user.email;
      const userGroups = userGroupsData.filter((group: any) => 
        group.users?.some((u: any) => u.email === currentUserEmail)
      );
      const groupIds = userGroups.map((g: any) => g.id);

      setHomeScreenItems(items);
      setAgents(agentsData);
      setEmbeddedContents(embeddedData);
      setUserGroupIds(groupIds);
      setLanguages(languagesData);
    } catch (err) {
      console.error('Failed to load home screen:', err);
      setError('Failed to load home screen');
    } finally {
      setLoading(false);
    }
  };

  // const getIconComponent = (iconName: string) => {
  //   const Icon = (LucideIcons as any)[iconName];
  //   return Icon || LucideIcons.Bot;
  // };

  const hasAccessToItem = (item: HomeScreenItem): boolean => {
    // If no group access defined, everyone has access
    if (!item.groupAccess || item.groupAccess.length === 0) {
      return true;
    }

    // Parse groupAccess if it's a JSON string
    let accessGroups: string[] = [];
    if (typeof item.groupAccess === 'string') {
      try {
        const parsed = JSON.parse(item.groupAccess);
        // Ensure the parsed value is an array
        accessGroups = Array.isArray(parsed) ? parsed : [];
      } catch {
        accessGroups = [];
      }
    } else if (Array.isArray(item.groupAccess)) {
      accessGroups = item.groupAccess;
    } else {
      // If it's neither a string nor an array, treat as no restrictions
      return true;
    }

    // If accessGroups is empty after parsing, everyone has access
    if (accessGroups.length === 0) {
      return true;
    }

    // Check if user is in any of the allowed groups
    return accessGroups.some(groupId => userGroupIds.includes(groupId));
  };

  const handleAgentClick = (agentId: string) => {
    navigate(`/agent/${agentId}`);
  };

  const handleEmbeddedClick = (embeddedContentId: string) => {
    navigate(`/embedded/${embeddedContentId}`);
  };

  const renderItem = useCallback((item: HomeScreenItem) => {
    if (item.type === 'agent' && item.agentId) {
      const agent = agents.find(a => a.id === item.agentId);
      if (!agent) return null;

      const IconComponent = getIconComponent(agent.icon);
      const translation = getAgentTranslation(agent, currentLanguage, languages);
      
      return (
        <button
          key={item.id}
          onClick={() => handleAgentClick(agent.id)}
          className="w-40 h-40 p-2 border-2 border-border rounded-lg bg-card flex flex-col items-center justify-center text-center gap-3 hover:border-primary hover:bg-card/80 transition-colors cursor-pointer"
          style={{ borderColor: `${agent.color}40` }}
        >
          <IconComponent className="w-10 h-10" style={{ color: agent.color }} />
          <span className="font-medium line-clamp-2">{translation.name}</span>
          {translation.description && (
            <span className="text-xs text-muted-foreground line-clamp-2">{translation.description}</span>
          )}
        </button>
      );
    }

    if (item.type === 'embedded' && item.embeddedContentId) {
      const embeddedContent = embeddedContents.find(e => e.id === item.embeddedContentId);
      if (!embeddedContent) return null;

      const IconComponent = getIconComponent(embeddedContent.icon);
      return (
        <button
          key={item.id}
          onClick={() => handleEmbeddedClick(embeddedContent.id)}
          className="w-40 h-40 p-4 border-2 border-border rounded-lg bg-card flex flex-col items-center justify-center text-center gap-3 hover:border-primary hover:bg-card/80 transition-colors cursor-pointer"
          style={{ borderColor: `${embeddedContent.color}40` }}
        >
          <IconComponent className="w-10 h-10" style={{ color: embeddedContent.color }} />
          <span className="font-medium line-clamp-2">{embeddedContent.name}</span>
          {embeddedContent.description && (
            <span className="text-xs text-muted-foreground line-clamp-2">{embeddedContent.description}</span>
          )}
        </button>
      );
    }

    if (item.type === 'grouping-container') {
      // Check if the group itself has access restrictions
      if (!hasAccessToItem(item)) {
        return null;
      }

      const children = homeScreenItems
        .filter(child => child.parentId === item.id && hasAccessToItem(child))
        .sort((a, b) => a.order - b.order);

      if (children.length === 0) {
        return null;
      }

      // Calculate grid size for minimum height
      const maxColumns = 4;
      const rows = Math.ceil(children.length / maxColumns);
      const minHeight = Math.max(120, rows * 144);

      return (
        <div key={item.id} className="relative">
          <div className="relative">
            {item.showTitle !== false && item.name && (
              <div className="absolute -top-4 left-4 px-2 bg-background z-10 flex items-center space-x-2">
                <span className="font-medium text-lg">{item.name}</span>
              </div>
            )}
            <div className="border-2 border-border rounded-lg bg-card">
              <div style={{ minHeight: `${minHeight}px` }} className="p-3">
                <div className="flex flex-row flex-wrap gap-3">
                  {children.map(child => (
                    <div key={child.id}>
                      {renderItem(child)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  }, [agents, embeddedContents, homeScreenItems, currentLanguage, languages]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2Icon className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">{t('home.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  // Filter top-level items that the user has access to
  const topLevelItems = homeScreenItems
    .filter(item => !item.parentId && hasAccessToItem(item))
    .sort((a, b) => a.order - b.order);

  if (topLevelItems.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground">{t('home.noItems')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="w-full">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2 text-center">{t('home.welcome')}</h1>
          <p className="text-muted-foreground text-center">{t('home.chooseOption')}</p>
        </div>

        <div className="flex flex-row flex-wrap gap-4 justify-center items-center">
          {topLevelItems.map(item => renderItem(item))}
        </div>
      </div>
    </div>
  );
};

